// 通用图片上传监控工具
import { feBot } from './fe_bot';
import { getFileBase64 } from '@/utils/someUtils';
import { v4 as uuidv4 } from 'uuid';
import { type EventMap } from '@/posthog/constants';
import { RPostHog } from '@/posthog/report';
import {
  isMobile,
  browserName,
  browserVersion,
  osName,
  osVersion,
} from 'react-device-detect';

const getFileSize = (file: File) => `${(file.size / 1024).toFixed(2)} KB`;

// 每一步的监控信息结构
interface StepLog {
  step: string;
  info?: Record<string, any>;
  time: string;
}

// 单张图片的监控上下文
interface ImageUploadContext {
  title?: string;
  contextId: string;
  fileInfo: {
    name: string;
    size: string;
    type: string;
    base64?: string;
  };
  userAgent: {
    isMobile: boolean;
    browserName: string;
    browserVersion: string;
    osName: string;
    osVersion: string;
  };
  from?: string;
  steps: StepLog[];
  error?: any;
}

// 全局上下文Map
const contextMap = new Map<string, ImageUploadContext>();

// 当前用户id
let userId: string | undefined;

export class ImageUploadMonitor {
  /**
   * 初始化监控，返回contextId
   */
  static async startMonitor(
    file: File,
    extra: { from?: string; contextId?: string; title?: string } = {}
  ) {
    const contextId = extra.contextId || uuidv4();
    const base64 = await getFileBase64(file, 100);
    const ctx: ImageUploadContext = {
      contextId,
      fileInfo: {
        name: file.name,
        size: getFileSize(file),
        type: file.type,
        base64,
      },
      userAgent: {
        isMobile,
        browserName,
        browserVersion,
        osName,
        osVersion,
      },
      from: extra.from,
      title: extra.title,
      steps: [
        {
          step: 'select',
          info: {
            name: file.name,
            size: getFileSize(file),
            type: file.type,
            base64,
          },
          time: new Date().toISOString(),
        },
      ],
    };
    contextMap.set(contextId, ctx);
    return contextId;
  }

  /**
   * 记录流程中的每一步
   */
  static async logStep({
    contextId,
    step,
    info = {},
    file,
  }: {
    contextId: string;
    step: string;
    info?: Record<string, any>;
    file?: File;
  }) {
    const ctx = contextMap.get(contextId);
    if (!ctx) return;
    ctx.steps.push({
      step,
      info: {
        ...(file && {
          name: file.name,
          size: getFileSize(file),
          type: file.type,
          base64: await getFileBase64(file, 100),
        }),
        ...info,
      },
      time: new Date().toISOString(),
    });
  }

  /**
   * 上传失败时统一上报
   */
  static async reportError(contextId: string, error: any) {
    const ctx = contextMap.get(contextId);
    if (!ctx) return;
    ctx.error = error;
    // 整理上报内容
    const errorInfo = typeof error === 'string' ? error : JSON.stringify(error);
    const otherInfo = JSON.stringify({
      fileInfo: ctx.fileInfo,
      steps: ctx.steps,
      userAgent: ctx.userAgent,
      // 添加 contextId，用于将 bot 错误和 posthog 错误关联
      contextId,
    });
    await feBot.lark_report({
      title: ctx.title || 'rmw_image_upload',
      type: ctx.from || '',
      errorInfo,
      uid: userId || '',
      otherInfo,
    });
    contextMap.delete(contextId);
  }

  static setUserId(curUserId: string) {
    userId = curUserId;
  }

  /**
   * 上传成功或流程结束时清理上下文
   */
  static endMonitor(contextId: string) {
    contextMap.delete(contextId);
  }

  /**
   * 获取当前上下文（调试用）
   */
  static getContext(contextId: string) {
    return contextMap.get(contextId);
  }

  // static reportImageUploadFail = ({
  //   contextId,
  //   error,
  // }: Pick<EventMap['batch_rmw_image_upload_fail'], 'error' | 'contextId'>) => {
  //   const ctx = contextMap.get(contextId);
  //   if (!ctx) return;
  //   RPostHog('batch_rmw_image_upload_fail', {
  //     contextId,
  //     error,
  //     ...(ctx.fileInfo && { fileInfo: JSON.stringify(ctx.fileInfo) }),
  //     ...(ctx.steps && { steps: JSON.stringify(ctx.steps) }),
  //   });
  // };
}
